import { useEffect, useRef, useState } from 'react';

import { BackrsForm } from '@backrs_ui/common-ui/src/molecules/BackrsForm/BackrsForm';
import {
  BackrsFormField,
  BackrsFormValues,
} from '@backrs_ui/common-ui/src/molecules/BackrsForm/types';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useValidateRegistrationMutation } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Typography, Theme, SxProps, Checkbox, FormControlLabel } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

const STRINGS = appStrings.modules.signup;

const defaultSignUpValues: BackrsFormValues = {
  name: '',
  phone: '',
  email: '',
  password: '',
  is_fourteen_plus: 'true',
};

export type SignUpFormData = Readonly<{
  name: string;
  phone: string;
  email: string;
  password: string;
  is_fourteen_plus: string;
}>;

type SignUpFormProps = Readonly<{
  onSignUpComplete: (data: SignUpFormData) => void | Promise<void>;
  onLoginClick: () => void;
  initialEmail?: string | null;
  isFamilyInvite?: boolean;
  buttonLabel?: string;
  buttonId?: string;
  loginLinkId?: string;
}>;

export function SignUpForm({
  onSignUpComplete,
  onLoginClick,
  initialEmail = null,
  isFamilyInvite = false,
  buttonLabel = 'Get started',
  buttonId = 'signup-get-started-button',
  loginLinkId = 'signup-login-link',
}: SignUpFormProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailChangedFromInvite, setEmailChangedFromInvite] = useState(false);
  const passwordTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [validateRegistration] = useValidateRegistrationMutation();

  const methods = useForm<BackrsFormValues>({
    defaultValues: {
      ...defaultSignUpValues,
      email: initialEmail || '',
    },
    mode: 'onSubmit',
  });

  // Track email changes for family invite users
  const handleEmailChange = (value: string) => {
    if (!isFamilyInvite || !initialEmail) return;

    const normalizedValue = value.toLowerCase().trim();
    const normalizedInitial = initialEmail.toLowerCase().trim();

    setEmailChangedFromInvite(normalizedValue !== normalizedInitial);
  };

  useEffect(() => {
    return () => {
      if (passwordTimeoutRef.current) {
        clearTimeout(passwordTimeoutRef.current);
      }
    };
  }, []);

  const fields: BackrsFormField[] = [
    {
      id: 'name',
      name: 'name',
      placeholder: 'Enter your full name',
      type: 'text',
      autoComplete: 'name',
      rules: {
        required: 'Name is required',
        validate: (value: string) => {
          const trimmed = value?.trim() || '';
          const spaceIndex = trimmed.indexOf(' ');

          // Must have a space with text on both sides
          if (spaceIndex <= 0 || spaceIndex >= trimmed.length - 1) {
            return 'Please enter your first and last name';
          }
          // Check that there's actual text after the space (not just whitespace)
          if (!trimmed.substring(spaceIndex + 1).trim()) {
            return 'Please enter your first and last name';
          }

          return true;
        },
      },
      autoFocus: true,
    },
    {
      id: 'phone',
      name: 'phone',
      placeholder: 'Enter your phone number',
      type: 'tel',
      autoComplete: 'tel',
      rules: {
        required: 'Phone number is required',
        pattern: {
          value: /^\d{10}$/,
          message: 'Please enter a valid 10-digit phone number',
        },
      },
    },
    {
      id: 'email',
      name: 'email',
      placeholder: 'Enter your email',
      type: 'email',
      autoComplete: 'email',
      helperText:
        isFamilyInvite && emailChangedFromInvite
          ? 'Note: Using a different email may prevent you from joining the family plan'
          : undefined,
      rules: {
        required: 'Email is required',
        pattern: {
          value: /^\S+@\S+\.\S+$/,
          message: 'Please enter a valid email address',
        },
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleEmailChange(e.target.value),
      },
    },
    {
      id: 'password',
      name: 'password',
      placeholder: 'Create a password',
      type: isPasswordVisible ? 'text' : 'password',
      autoComplete: 'new-password',
      helperText: 'Must be at least 8 characters.',
      rules: {
        required: 'Password is required',
        minLength: { value: 8, message: 'Must be at least 8 characters' },
      },
      icon: {
        type: 'RIGHT_ICON',
        rightIcon: isPasswordVisible ? 'EyeOff' : 'Eye',
        onRightIconClick: () => {
          if (passwordTimeoutRef.current) {
            clearTimeout(passwordTimeoutRef.current);
          }

          setIsPasswordVisible((prev) => !prev);
          passwordTimeoutRef.current = setTimeout(() => {
            setIsPasswordVisible(false);
          }, 5000);
        },
      },
    },
  ];

  const handleSubmission = async (data: BackrsFormValues) => {
    setIsSubmitting(true);

    try {
      // Validate email and phone for uniqueness before proceeding
      await validateRegistration({
        email: data.email as string,
        phone: data.phone as string,
      }).unwrap();

      // Validation passed - proceed to onboarding
      await onSignUpComplete({
        name: data.name,
        phone: data.phone,
        email: data.email,
        password: data.password,
        is_fourteen_plus: data.is_fourteen_plus,
      });
    } catch (error) {
      const apiError = error as { data?: Record<string, string> };

      if (apiError.data) {
        // Set field-specific errors
        Object.entries(apiError.data).forEach(([field, message]) => {
          methods.setError(field as keyof BackrsFormValues, {
            type: 'manual',
            message: message as string,
          });
        });
      } else {
        methods.setError('root', {
          type: 'manual',
          message: 'An error occurred. Please try again.',
        });
      }

      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={formContainerSx}>
      <Box sx={inputOverridesSx}>
        <BackrsForm
          fields={fields}
          methods={methods}
          onSubmit={handleSubmission}
          buttonLabel={buttonLabel}
          buttonId={buttonId}
          isLoading={isSubmitting}
        >
          <Controller
            name="is_fourteen_plus"
            control={methods.control}
            rules={{
              required: 'You must be 14 years or older to use this service',
            }}
            render={({ field, fieldState }) => (
              <Box sx={checkboxContainerStyle}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="signup-age-checkbox"
                      checked={field.value === 'true'}
                      onChange={(e) => {
                        field.onChange(e.target.checked ? 'true' : '');
                      }}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckIcon />}
                    />
                  }
                  label={
                    <Typography variant="text-sm-regular" sx={checkboxLabelTextStyle}>
                      {STRINGS.fourteenPlus}
                    </Typography>
                  }
                  sx={checkboxStyle}
                />
                {fieldState.error && (
                  <Typography variant="text-sm-regular" sx={checkboxErrorStyle}>
                    {fieldState.error.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </BackrsForm>
      </Box>
      {methods.formState.errors.root && (
        <Box sx={errorMessageSx}>{methods.formState.errors.root.message}</Box>
      )}
      <Box sx={loginRowSx}>
        <Typography component="span" variant="text-sm-regular">
          {STRINGS.haveAccount}{' '}
        </Typography>
        <Typography
          id={loginLinkId}
          data-ph-capture-attribute-name={loginLinkId}
          component="span"
          variant="text-sm-semibold"
          sx={embeddedButtonSx}
          onClick={onLoginClick}
        >
          {STRINGS.login}
        </Typography>
      </Box>
    </Box>
  );
}

const formContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: backrs.spacing['spacing-3xl'],
});

const loginRowSx: SxProps<Theme> = {
  textAlign: 'center',
};

const inputOverridesSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root:not(.Mui-focused)': {
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  },
  '& .MuiOutlinedInput-root': {
    height: '44px',
    marginBottom: backrs.spacing['spacing-xs'],
  },
});

const errorMessageSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const embeddedButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline',
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
  cursor: 'pointer',
});

const checkboxContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  mt: '4px',
  ml: '-4px',
};

const checkboxStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  outline: 'none',
  padding: backrs.spacing['spacing-none'],
  '&.Mui-disabled .MuiFormControlLabel-label': {
    color: backrs.colors['Colors/Base/Black'],
  },
  '& .MuiFormControlLabel-label': {
    ...typography['text-xs-regular'],
    marginTop: backrs.spacing['spacing-none'],
    paddingTop: backrs.spacing['spacing-none'],
  },
  '& .MuiFormControlLabel-root': {
    display: 'flex',
    marginTop: backrs.spacing['spacing-none'],
    paddingTop: backrs.spacing['spacing-none'],
  },
  '& .MuiCheckbox-root': {
    marginRight: backrs.spacing['spacing-lg'],
    alignSelf: 'flex-start',
  },
  marginLeft: backrs.spacing['spacing-md'],
});

const checkboxLabelTextStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  ml: '-4px',
});

const checkboxErrorStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
  marginTop: backrs.spacing['spacing-xs'],
  marginLeft: backrs.spacing['spacing-5xl'], // Align with text (checkbox width + margin)
});
